// Shared, framework-agnostic parsing + rendering for the blog `:::timeline`
// construct. The raw `:::timeline … :::` source is the single source of truth:
// both the TipTap node's `renderHTML` (canonical stored HTML) and the React
// NodeView (live editor preview) derive their structure from `parseTimelineSource`.

export type TimelineItem = { year: string; body: string }
export type TimelineEra = { label: string; span?: string; items: TimelineItem[] }

// ProseMirror DOM-spec node: [tag, attrs?, ...children]. Children are nested
// specs or plain strings (escaped as text). Matches @tiptap/core's DOMOutputSpec.
type DomSpecChild = string | DomSpec
export type DomSpec = [string, Record<string, string>, ...DomSpecChild[]] | [string, Record<string, string>]

const FENCE_OPEN = /^:::timeline\s*$/i
const FENCE_CLOSE = /^:::\s*$/

/**
 * Parse a raw `:::timeline` block into era groups. Accepts source with or
 * without the surrounding fence lines (paste includes them; the round-tripped
 * `data-timeline-source` stores the inner block). Never throws — malformed
 * lines are skipped so a single bad line can't break the post.
 */
export function parseTimelineSource(source: string): TimelineEra[] {
  const eras: TimelineEra[] = []
  let current: TimelineEra | null = null

  const ensureEra = (): TimelineEra => {
    if (!current) {
      current = { label: '', items: [] }
      eras.push(current)
    }
    return current
  }

  for (const rawLine of String(source ?? '').split(/\r?\n/)) {
    const line = rawLine.trim()
    if (!line) continue
    if (FENCE_OPEN.test(line) || FENCE_CLOSE.test(line)) continue

    if (line.startsWith('##')) {
      const heading = line.slice(2).trim()
      if (!heading) continue
      const pipe = heading.indexOf('|')
      const label = (pipe === -1 ? heading : heading.slice(0, pipe)).trim()
      const span = pipe === -1 ? undefined : heading.slice(pipe + 1).trim() || undefined
      current = { label, span, items: [] }
      eras.push(current)
      continue
    }

    // `YEAR | TEXT` — split on the first pipe only (body may contain pipes).
    const pipe = line.indexOf('|')
    if (pipe === -1) continue // malformed: no separator — skip gracefully
    const year = line.slice(0, pipe).trim()
    const body = line.slice(pipe + 1).trim()
    if (!year || !body) continue
    ensureEra().items.push({ year, body })
  }

  // Drop eras that ended up with no label and no items (defensive).
  return eras.filter((era) => era.label || era.items.length > 0)
}

/**
 * Build the canonical ProseMirror DOM spec for a timeline. Drives
 * `editor.getHTML()` serialization, so the stored/published HTML matches the
 * contract exactly. The raw `source` is preserved on `data-timeline-source`
 * for round-tripping back into the editor.
 */
export function timelineToDomSpec(source: string): DomSpec {
  const eras = parseTimelineSource(source)
  const children: DomSpec[] = []

  for (const era of eras) {
    const eraChildren: DomSpec[] = [['span', { class: 'gs-timeline__era-label' }, era.label]]
    if (era.span) {
      eraChildren.push(['span', { class: 'gs-timeline__era-span' }, era.span])
    }
    children.push(['div', { class: 'gs-timeline__era' }, ...eraChildren])

    const items: DomSpec[] = era.items.map((item) => [
      'li',
      { class: 'gs-timeline__item' },
      ['span', { class: 'gs-timeline__node', 'aria-hidden': 'true' }],
      ['span', { class: 'gs-timeline__year' }, item.year],
      ['p', { class: 'gs-timeline__body' }, item.body],
    ])
    children.push(['ol', { class: 'gs-timeline__items' }, ...items])
  }

  return [
    'section',
    { class: 'gs-timeline', 'aria-label': 'Timeline', 'data-timeline-source': String(source ?? '') },
    ...children,
  ]
}
